import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import {ClipLoader} from 'react-spinners'
import emailValidator from 'email-validator'

import {sendForm} from '../../utils/forms'
import {withTheme} from 'emotion-theming'
import {SubmitButton} from '../Button'

const RootContainer = styled.form`
`

const SubmitButtonStyled = styled(SubmitButton)`
	width: 100%;
	margin-top: 1.25rem;
`

const LoadingContainer = styled.div`
	display: flex;
	margin-top: 1.25rem;
	justify-content: center;
`

const DoneMessage = styled.div`
	margin-top: 1.25rem;
	color: ForestGreen;
`

const ErrorMessage = styled.div`
	margin-top: 1.25rem;
	color: FireBrick;
`

function SubmittableForm({className, submitElement: SubmitElement, formName, children, values, theme, clearValues, submitLabel, shouldPreventSubmit}) {
	const [submitState, setSubmitState] = useState({
		loading: false,
		error: null,
		success: false,
	})

	const submitData = async () => {
		try {
			await sendForm({formName, formContent: values})
			setSubmitState({loading: false, success: true, error: null})
		} catch (error) {
			setSubmitState({loading: false, success: false, error})
		}
	}

	useEffect(() => {
		if (!submitState.loading) return
		submitData()
	}, [submitState.loading])

	useEffect(() => {
		if (!submitState.success) return
		setTimeout(() => {
			setSubmitState({loading: false, error: null, success: false})
			clearValues()
		}, 5000)
	}, [submitState.success])

	const onSubmit = e => {
		e.preventDefault()
		if (shouldPreventSubmit()) return

		setSubmitState({loading: true, error: null, success: false})
	}

	return <RootContainer noValidate onSubmit={onSubmit} className={className}>
		{children}
		{submitState.error &&
		<ErrorMessage>{submitState.error.message}</ErrorMessage>}
		{(!submitState.loading && !submitState.success) && <SubmitElement>{submitLabel}</SubmitElement>}
		{submitState.loading && <LoadingContainer>
			<ClipLoader color={theme.colors.main}/>
		</LoadingContainer>}
		{submitState.success && <DoneMessage>Form was successfully submitted. Thank you.</DoneMessage>}
	</RootContainer>
}

SubmittableForm.propTypes = {
	className: PropTypes.string,
	shouldPreventSubmit: PropTypes.func,
}

SubmittableForm.defaultProps = {
	className: '',
	shouldPreventSubmit: () => false,
	submitElement: SubmitButtonStyled,
}

export default withTheme(SubmittableForm)

export function useFormValuesStorage({fields}) {
	const fieldNames = fields.map(one => one.placeholder)
	const generateEmpty = () => fieldNames.reduce((acc, value) => ({...acc, [value]: ''}), {})
	const [values, setValues] = useState(generateEmpty())
	const errors = validate({fields, values})


	return {
		setValue: ({fieldName, value}) => {
			setValues((values) => ({...values, [fieldName]: value}))
		},
		clearValues: () => setValues(generateEmpty()),
		values,
		errors,
		isInvalid: Object.keys(errors).some(key => errors[key].length > 0),
	}
}

export function validate({fields, values}) {
	const errors = fields.map(field => {
		const {placeholder, required, type} = field
		const value = values[field.placeholder]

		let errorMessages = []
		if (required && !value) errorMessages.push(`${placeholder} ${type === 'checkbox' ? 'must be checked.' : 'must be filled.'}`)
		if (type === 'email' && !!value && !emailValidator.validate(value)) errorMessages.push(`${placeholder} must be a correct email address`)
		return {
			placeholder,
			errorMessages,
		}
	})
	return errors.reduce((acc, {placeholder, errorMessages}) => ({...acc, [placeholder]: errorMessages}), {})
}
